import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/layout/Layout';
import LeadList from '../components/leads/LeadList';
import LeadForm from '../components/leads/LeadForm';
import LeadDetail from '../components/leads/LeadDetail';
import {
  Squares2X2Icon,
  TableCellsIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { leadsAPI, leadStagesAPI, api } from '../services/api';
import { useCompany } from '../contexts/CompanyContext';
import { useSocket } from '../contexts/SocketContext';
import toast from 'react-hot-toast';

const LeadsPage = () => {
  const [currentView, setCurrentView] = useState('list'); // 'list', 'create', 'edit', 'detail'
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'card'
  const [stats, setStats] = useState({ totalLeads: 0, activeLeads: 0, convertedLeads: 0, stageStats: {} });
  const [leadStages, setLeadStages] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [showStageStats, setShowStageStats] = useState(false);
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingLead, setDeletingLead] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { socket, isConnected } = useSocket();

  useEffect(() => {
    // Check if we should show a specific lead detail
    const leadIdFromUrl = id || searchParams.get('id');
    if (leadIdFromUrl) {
      setSelectedLeadId(leadIdFromUrl);
      setCurrentView('detail');
    }
    fetchStats();
    fetchLeadStages();
  }, [id, searchParams]);

  useEffect(() => {
    if (!socket || !isConnected) {
      return undefined;
    }

    const handleLeadCreated = (data) => {
      fetchStats();
      setRefreshTrigger((previous) => previous + 1);

      if (currentView === 'list') {
        toast.success(data?.notification?.message || 'A new lead was received.');
      }
    };

    const handleLeadUpdated = (data) => {
      fetchStats();
      setRefreshTrigger((previous) => previous + 1);

      if (currentView === 'list' && data?.origin === 'webhook') {
        toast.success(data?.notification?.message || 'A webhook lead was updated.');
      }
    };

    socket.on('lead-created', handleLeadCreated);
    socket.on('lead-updated', handleLeadUpdated);

    return () => {
      socket.off('lead-created', handleLeadCreated);
      socket.off('lead-updated', handleLeadUpdated);
    };
  }, [socket, isConnected, currentView]);

  const fetchStats = async () => {
    try {
      // Fetch all leads and lead stages
      const [leadsResponse, stagesResponse] = await Promise.all([
        leadsAPI.getAll({ limit: 1000 }), // Get all leads for accurate stats
        leadStagesAPI.getAll()
      ]);

      const leads = leadsResponse.data.leads || [];
      const stages = stagesResponse.data.leadStages || [];

      // Find stages with isWon: true
      const wonStages = stages.filter(stage => stage.isWon === true);
      const wonStageIds = wonStages.map(stage => stage._id);

      // Calculate stats
      const totalLeads = leads.length;
      const activeLeads = leads.filter(lead => lead.status === 'active').length;
      const convertedLeads = leads.filter(lead =>
        wonStageIds.includes(lead.stage?._id || lead.stage)
      ).length;

      // Calculate conversion percentage
      const conversionPercentage = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;

      // Calculate stage-wise stats
      const stageStats = {};
      stages.forEach(stage => {
        const stageLeads = leads.filter(lead =>
          (lead.stage?._id || lead.stage) === stage._id
        );
        stageStats[stage._id] = {
          name: stage.name,
          count: stageLeads.length,
          color: stage.color,
          isWon: stage.isWon
        };
      });

      setStats({
        totalLeads,
        activeLeads,
        convertedLeads,
        conversionPercentage: Math.round(conversionPercentage * 100) / 100, // Round to 2 decimal places
        stageStats
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({ totalLeads: 0, activeLeads: 0, convertedLeads: 0, conversionPercentage: 0, stageStats: {} });
    }
  };

  const fetchLeadStages = async () => {
    try {
      const response = await leadStagesAPI.getAll();
      setLeadStages(response.data.leadStages || []);
    } catch (error) {
      console.error('Error fetching lead stages:', error);
    }
  };

  const toggleStageExpansion = (stageId) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageId]: !prev[stageId]
    }));
  };

  const handleCreateLead = () => {
    setSelectedLeadId(null);
    setCurrentView('create');
  };

  const handleEditLead = (leadId) => {
    setSelectedLeadId(leadId);
    setCurrentView('edit');
  };

  const handleViewLead = (leadId) => {
    setSelectedLeadId(leadId);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedLeadId(null);
    // Clear URL parameters
    window.history.replaceState(null, '', '/leads');
  };

  const handleLeadUpdate = () => {
    fetchStats(); // Refresh stats when leads are updated
  };

  const handleLeadDelete = () => {
    fetchStats(); // Refresh stats when leads are deleted
  };

  const handleFormSuccess = () => {
    setCurrentView('list');
    setSelectedLeadId(null);
    fetchStats(); // Refresh stats after form submission
    fetchLeadStages(); // Refresh stages in case they were modified
    // Clear URL parameters
    window.history.replaceState(null, '', '/leads');
  };

  // Modal handlers
  const handleDeleteLead = (lead) => {
    setDeletingLead(lead);
    setShowDeleteModal(true);
  };

  const confirmDeleteLead = async () => {
    if (!deletingLead) return;

    try {
      await leadsAPI.delete(deletingLead._id);
      setShowDeleteModal(false);
      setDeletingLead(null);
      fetchStats(); // Refresh stats after deletion
      handleLeadUpdate(); // Refresh lead list data
      setRefreshTrigger(prev => prev + 1); // Trigger lead list refresh
      toast.success('Lead deleted successfully');
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async (selectedLeads) => {
    try {
      await Promise.all(selectedLeads.map(id => leadsAPI.delete(id)));
      setShowBulkDeleteModal(false);
      fetchStats(); // Refresh stats after deletion
      handleLeadUpdate(); // Refresh lead list data
      setRefreshTrigger(prev => prev + 1); // Trigger lead list refresh
      toast.success(`${selectedLeads.length} leads deleted successfully`);
    } catch (error) {
      console.error('Error deleting leads:', error);
      toast.error('Failed to delete some leads');
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'create':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <LeadForm
              onSuccess={handleFormSuccess}
              onCancel={handleBackToList}
            />
          </motion.div>
        );
      case 'edit':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <LeadForm
              leadId={selectedLeadId}
              onSuccess={handleFormSuccess}
              onCancel={handleBackToList}
            />
          </motion.div>
        );
      case 'detail':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <LeadDetail
              leadId={selectedLeadId}
              onBack={handleBackToList}
              onEdit={handleEditLead}
            />
          </motion.div>
        );
      default:
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="space-y-3 sm:space-y-4 lg:space-y-5"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              {/* Total Leads Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-2 sm:p-3 text-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-xs font-medium">Total Leads</p>
                    <p className="text-lg sm:text-xl font-bold">{stats.totalLeads || 0}</p>
                  </div>
                  <div className="bg-blue-400/20 p-1 rounded-md">
                    <UserGroupIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                  </div>
                </div>
              </motion.div>

              {/* Active Leads Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-2 sm:p-3 text-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-xs font-medium">Active Leads</p>
                    <p className="text-lg sm:text-xl font-bold">{stats.activeLeads || 0}</p>
                  </div>
                  <div className="bg-emerald-400/20 p-1 rounded-md">
                    <SparklesIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                  </div>
                </div>
              </motion.div>

              {/* Converted Leads Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-2 sm:p-3 text-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-xs font-medium">Converted</p>
                    <p className="text-lg sm:text-xl font-bold">{stats.convertedLeads || 0}</p>
                  </div>
                  <div className="bg-amber-400/20 p-1 rounded-md">
                    <ChartBarIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                  </div>
                </div>
              </motion.div>

              {/* Conversion Rate Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-2 sm:p-3 text-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-xs font-medium">Conversion Rate</p>
                    <p className="text-lg sm:text-xl font-bold">{stats.conversionPercentage || 0}%</p>
                  </div>
                  <div className="bg-purple-400/20 p-1 rounded-md">
                    <BuildingOfficeIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Dynamic Stage Stats Cards - Accordion */}
            <AnimatePresence>
              {showStageStats && leadStages.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="bg-white/80 backdrop-blur-xl rounded-lg shadow-lg border border-white/20 overflow-hidden"
                >
                  <div className="px-2 py-1.5 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50">
                    <h3 className="text-sm font-semibold text-gray-800">Leads by Stage</h3>
                  </div>

                  <div className="divide-y divide-gray-200/50">
                    {leadStages
                      .sort((a, b) => a.order - b.order)
                      .map((stage, index) => {
                        const stageCount = stats.stageStats?.[stage._id]?.count || 0;
                        const isExpanded = expandedStages[stage._id] || false;

                        return (
                          <motion.div
                            key={stage._id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 + index * 0.05 }}
                            className="group"
                          >
                            {/* Accordion Header */}
                            <button
                              onClick={() => toggleStageExpansion(stage._id)}
                              className="w-full px-2 py-1.5 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200"
                            >
                              <div className="flex items-center space-x-2">
                                <div
                                  className="w-2.5 h-2.5 rounded-full border border-white shadow-sm"
                                  style={{ backgroundColor: stage.color || '#6366f1' }}
                                />
                                <div className="text-left">
                                  <h4 className="text-sm font-semibold text-gray-900">{stage.name}</h4>
                                  <p className="text-xs text-gray-600">
                                    {stageCount} lead{stageCount !== 1 ? 's' : ''}
                                    {stage.probability > 0 && ` • ${stage.probability}% probability`}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-1">
                                <span className="text-sm font-bold text-gray-900">{stageCount}</span>
                                {isExpanded ? (
                                  <ChevronUpIcon className="h-3 w-3 text-gray-400 group-hover:text-gray-600 transition-colors" />
                                ) : (
                                  <ChevronDownIcon className="h-3 w-3 text-gray-400 group-hover:text-gray-600 transition-colors" />
                                )}
                              </div>
                            </button>

                            {/* Accordion Content */}
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-2 pb-1.5">
                                    <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-md p-1.5 border border-gray-200/50">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                                        <div>
                                          <div className="text-sm font-bold text-gray-900">{stageCount}</div>
                                          <div className="text-xs text-gray-600 uppercase tracking-wide">Total Leads</div>
                                        </div>
                                        {stage.isWon && (
                                          <div>
                                            <div className="text-sm font-bold text-green-600">
                                              {stats.stageStats?.[stage._id]?.count || 0}
                                            </div>
                                            <div className="text-xs text-gray-600 uppercase tracking-wide">Won</div>
                                          </div>
                                        )}
                                        {stage.isLost && (
                                          <div>
                                            <div className="text-sm font-bold text-red-600">
                                              {stats.stageStats?.[stage._id]?.count || 0}
                                            </div>
                                            <div className="text-xs text-gray-600 uppercase tracking-wide">Lost</div>
                                          </div>
                                        )}
                                        <div>
                                          <div className="text-sm font-bold text-blue-600">
                                            {stage.probability || 0}%
                                          </div>
                                          <div className="text-xs text-gray-600 uppercase tracking-wide">Probability</div>
                                        </div>
                                      </div>

                                      {stage.description && (
                                        <div className="mt-1.5 pt-1.5 border-t border-gray-200/50">
                                          <p className="text-xs text-gray-700">{stage.description}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20"
            >
              {/* Content */}
              <div className="px-3 sm:px-6 pb-3 sm:pb-6">
                <AnimatePresence mode="wait">
                  {viewMode === 'list' ? (
                    <motion.div
                      key="list"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <LeadList
                        onCreateLead={handleCreateLead}
                        onEditLead={handleEditLead}
                        onViewLead={handleViewLead}
                        onLeadUpdate={handleLeadUpdate}
                        onLeadDelete={handleLeadDelete}
                        onDeleteLead={handleDeleteLead}
                        onBulkDelete={handleBulkDelete}
                        refreshTrigger={refreshTrigger}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="card"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardView
                        onCreateLead={handleCreateLead}
                        onEditLead={handleEditLead}
                        onViewLead={handleViewLead}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        );
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pt-3 sm:pt-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 shadow-xl"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black/10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          <div className="relative px-4 py-3 sm:px-6 sm:py-2 lg:px-8 lg:py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                    {currentView === 'create' ? '✨ Create New Lead' : 
                     currentView === 'edit' ? '✏️ Edit Lead' : 
                     'Leads Management'}
                  </h1>
                  <p className="text-blue-100 text-sm lg:text-base max-w-xl">
                    {currentView === 'create' ? 'Fill in the details to create a new lead' :
                     currentView === 'edit' ? 'Update lead information and manage attachments' :
                     'Transform prospects into customers with intelligent lead tracking'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* View Toggle - Only show in list mode */}
                  {currentView === 'list' && (
                    <div className="flex items-center gap-2">
                      {/* Stage Stats Toggle */}
                      <motion.button
                        whileHover={{ scale: 1.08, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                        whileTap={{ scale: 0.92 }}
                        onClick={() => setShowStageStats(!showStageStats)}
                        className={`px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-300 flex items-center gap-2 ${
                          showStageStats
                            ? 'bg-white text-blue-600 shadow-lg'
                            : 'bg-white/10 text-blue-100 hover:text-white hover:bg-white/20 border border-white/30'
                        }`}
                        title={showStageStats ? 'Hide Stage Stats' : 'Show Stage Stats'}
                      >
                        {showStageStats ? (
                          <EyeIcon className="h-4 w-4" />
                        ) : (
                          <EyeSlashIcon className="h-4 w-4" />
                        )}
                        Stages
                      </motion.button>
                    </div>
                  )}

                  {currentView === 'list' && (
                    <motion.button
                      whileHover={{ scale: 1.05, boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.25)" }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCreateLead}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-lg text-blue-600 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create New Lead
                    </motion.button>
                  )}
                </div>
              </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className={`relative -mt-3 sm:-mt-4 pb-6 sm:pb-8 ${currentView === 'list' ? 'px-3 sm:px-4 lg:px-6' : ''}`}>
          <AnimatePresence mode="wait">
            {renderCurrentView()}
          </AnimatePresence>
        </div>
      </div>

      {/* Delete Confirmation Modals */}
      <AnimatePresence>
        {showDeleteModal && deletingLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-red-100 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>

                <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                  Delete Lead
                </h3>

                <p className="text-gray-600 text-center mb-4">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingLead.customer?.firstName || 'N/A'} {deletingLead.customer?.lastName || ''} ({deletingLead.leadno || 'N/A'})"</span>?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletingLead(null);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDeleteLead}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Delete Confirmation Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowBulkDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-red-100 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>

                <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                  Delete Leads
                </h3>

                <p className="text-gray-600 text-center mb-4">
                  Are you sure you want to delete all selected leads?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowBulkDeleteModal(false)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // This will be handled by the parent component
                      setShowBulkDeleteModal(false);
                    }}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete All
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

// Card View Component
const CardView = ({ onCreateLead, onEditLead, onViewLead }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [leadStages, setLeadStages] = useState([]);

  const { companyData } = useCompany();

  React.useEffect(() => {
    fetchLeads();
    fetchLeadStages();
  }, [stageFilter]);

  const fetchLeads = async () => {
    try {
      const params = stageFilter !== 'all' ? { stage: stageFilter } : {};
      const response = await leadsAPI.getAll(params);
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadStages = async () => {
    try {
      const response = await leadStagesAPI.getAll();
      setLeadStages(response.data.leadStages || []);
    } catch (error) {
      console.error('Error fetching lead stages:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await leadsAPI.delete(leadId);
        setLeads(leads.filter(lead => lead._id !== leadId));
      } catch (error) {
        console.error('Error deleting lead:', error);
      }
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchTerm === '' ||
      (lead.firstName + ' ' + lead.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.companyName && lead.companyName.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const getStageById = (id) => leadStages.find(s => String(s._id) === String(id));
  const getStageName = (stage) => {
    if (!stage) return 'Unknown';
    const id = typeof stage === 'string' ? stage : (stage._id || stage);
    return getStageById(id)?.name || (stage.name || 'Unknown');
  };
  const getStageColor = (stage) => {
    const id = stage && (typeof stage === 'string' ? stage : (stage._id || stage));
    return getStageById(id)?.color || '#6B7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 w-full"
                  />
                </div>
              </div>

              {/* Stage Filter */}
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Stages</option>
                {leadStages.map(stage => (
                  <option key={stage._id} value={stage._id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="px-6 py-3 text-sm text-gray-600">
          Showing <span className="font-bold text-gray-900">{filteredLeads.length}</span> of{' '}
          <span className="font-bold text-gray-900">{leads.length}</span> leads
        </div>
      </div>

      {/* Cards Grid */}
      <motion.div
        layout
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
      >
        <AnimatePresence>
          {filteredLeads.map((lead, index) => (
            <motion.div
              key={lead._id}
              layout
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.9 }}
              transition={{
                delay: index * 0.1,
                duration: 0.3,
                layout: { duration: 0.3 }
              }}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden group"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 p-6 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
                  }} />
                </div>

                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30"
                    >
                      <span className="text-xl font-bold text-white">
                        {lead.firstName?.charAt(0).toUpperCase() || lead.name?.charAt(0).toUpperCase() || '?'}
                      </span>
                    </motion.div>
                    <div>
                      <h3 className="text-xl font-bold text-white leading-tight">
                        {lead.firstName} {lead.lastName}
                      </h3>
                      <p className="text-blue-100 text-sm font-medium">
                        {lead.jobTitle || 'No Position'}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onViewLead(lead._id)}
                      className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors backdrop-blur-sm"
                      title="View Details"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onEditLead(lead._id)}
                      className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors backdrop-blur-sm"
                      title="Edit Lead"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDeleteLead(lead._id)}
                      className="p-2 text-white/80 hover:text-red-300 hover:bg-red-500/20 rounded-xl transition-colors backdrop-blur-sm"
                      title="Delete Lead"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-4 space-y-3">
                {/* Primary Info Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-700 truncate max-w-32">{lead.email || 'No email'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full text-white"
                      style={{ backgroundColor: getStageColor(lead.stage) }}
                    >
                      {getStageName(lead.stage)}
                    </span>
                  </div>
                </div>

                {/* Secondary Info Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-purple-500" />
                    <span className="text-sm text-gray-700 truncate max-w-24">{lead.companyName || 'No company'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                      lead.priority === 'High' ? 'bg-red-100 text-red-800' :
                      lead.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {lead.priority || 'Low'}
                    </span>
                  </div>
                </div>

                {/* Value and Assignment Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="text-sm font-bold text-amber-600">{companyData?.currency?.symbol || '$'}{lead.value || 0}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {lead.assignedTo ? (
                      <div className="flex items-center space-x-1">
                        <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-xs">
                            {lead.assignedTo.firstName?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-600 truncate max-w-16">{lead.assignedTo.firstName}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Unassigned</span>
                    )}
                  </div>
                </div>

                {/* Timeline Row */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Created {new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  {lead.lastContact && (
                    <span>Last: {new Date(lead.lastContact).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  )}
                </div>

                {/* Source and Custom Fields */}
                {(lead.source || (lead.customFields && Object.keys(lead.customFields).length > 0)) && (
                  <div className="flex flex-wrap gap-1">
                    {lead.source && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {lead.source}
                      </span>
                    )}
                    {lead.customFields && Object.entries(lead.customFields).slice(0, 2).map(([key, value]) => (
                      <span key={key} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {String(value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Empty State */}
      {filteredLeads.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16"
        >
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
            <UserGroupIcon className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No leads found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || stageFilter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first lead.'
            }
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onCreateLead}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create First Lead
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default LeadsPage;
